import {gql} from '@apollo/client';
import React from 'react';

import {bulkArchiveRequestTaskSchemas, bulkUnarchiveRequestTaskSchemas} from 'archive/ArchiveActions';
import useAreOrganizationSchemasCreatableQuery from 'auth/useAreOrganizationSchemasCreatableQuery';
import * as benchlingUtil from 'BenchlingUtil';
import BenchlingQuery, {QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import {getStore} from 'redux-stores/Store';
import * as RequestTaskSchemaDAO from 'requests/schemas/RequestTaskSchemaDAO';
import {RequestTaskSchema} from 'requests/tasks/RequestTaskConstants';
import SchemasListing from 'schemas/SchemasListing';
import {SCHEMA_TYPES} from 'schemas/spec/SchemaConfigConstants';

import {RequestTaskSchemasPageQuery} from './__generated/RequestTaskSchemasPageQuery';

interface RequestTaskSchemasPageProps {
  taskSchemas: Array<RequestTaskSchema> | null;
  areOrganizationSchemasCreatable: boolean;
}

export class RequestTaskSchemasPage extends React.Component<RequestTaskSchemasPageProps> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleNavigateToCreate = () => {
    benchlingUtil.navigate(null, RequestTaskSchemaDAO.taskSchemaCreateURL());
  };

  render(): JSX.Element {
    return (
      <SchemasListing
        schemas={this.props.taskSchemas}
        schemaType={SCHEMA_TYPES.REQUEST_TASK}
        navigateToCreate={this._handleNavigateToCreate}
        urlForSchema={RequestTaskSchemaDAO.taskSchemaURL}
        archiveSchemas={(ids: Array<string>, purpose: string) => {
          getStore().dispatch(bulkArchiveRequestTaskSchemas(ids, purpose));
        }}
        unarchiveSchemas={(ids: Array<string>) => {
          getStore().dispatch(bulkUnarchiveRequestTaskSchemas(ids));
        }}
        isWritable={this.props.areOrganizationSchemasCreatable}
      />
    );
  }
}

export const REQUEST_TASK_SCHEMAS_PAGE_QUERY = gql`
  query RequestTaskSchemasPageQuery @skipAudit @engComponent(name: REQUESTS) {
    ...AllRequestTaskSchemasGraphQL
  }
  ${getFragmentWrapper('AllRequestTaskSchemas').fragment}
`;

export default function QueryingRequestTaskSchemasPage(props: {}): JSX.Element {
  benchlingUtil.setWindowTitle('Task Schemas');
  const areOrganizationSchemasCreatableResults = useAreOrganizationSchemasCreatableQuery();
  const areOrganizationSchemasCreatable =
    areOrganizationSchemasCreatableResults.queryResultType === QueryResultType.SUCCESS &&
    areOrganizationSchemasCreatableResults.areOrganizationSchemasCreatable;

  return (
    <BenchlingQuery<RequestTaskSchemasPageQuery>
      query={REQUEST_TASK_SCHEMAS_PAGE_QUERY}
      renderLoading={() => (
        <RequestTaskSchemasPage
          taskSchemas={null}
          areOrganizationSchemasCreatable={areOrganizationSchemasCreatable}
        />
      )}
      fetchPolicy="cache-and-network"
    >
      {(data) => (
        <RequestTaskSchemasPage
          taskSchemas={graphQLArrayToREST('RequestTaskSchema', data.requestTaskSchemas)}
          areOrganizationSchemasCreatable={areOrganizationSchemasCreatable}
        />
      )}
    </BenchlingQuery>
  );
}
